/**
 * AttendeeAvatar — tiny round photo (or initials fallback) for an
 * attendee pill. 16px square by default; can be nudged via `size`.
 *
 * Used inside OJB member pills in AttendeesPanel, in the typeahead
 * dropdown in OjbMemberPicker, and (with onDark) in the header
 * UserMenu trigger pill. When no photo is known for the person
 * (roster miss, external attendee, free-typed name that doesn't
 * match Graph), falls back to an initials circle so the visual
 * rhythm stays consistent across the list.
 *
 * Variants:
 *   - default: pine-tinted fallback circle (pine/15 bg, pine text).
 *     Use on cream / cloud / bone surfaces.
 *   - onDark:  cream fallback circle (cream bg, pine text) with no
 *     ring. Use when the avatar sits on a pine fill (e.g. header
 *     UserMenu pill, pine-filled meeting rows). The fallback would
 *     otherwise disappear pine-on-pine.
 */
interface Props {
  name: string;
  headshotUrl: string | null;
  /** Pixel size — default 16. */
  size?: number;
  /** Set when the avatar sits on a pine fill so the fallback
   *  initials circle stays readable. Default false. */
  onDark?: boolean;
}

function initialsFor(name: string): string {
  const sansCreds = name.split(',')[0]?.trim() ?? name;
  const parts = sansCreds.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function AttendeeAvatar({
  name,
  headshotUrl,
  size = 16,
  onDark = false,
}: Props) {
  const style = { height: size, width: size };
  // Initials font-size scales loosely with the avatar — ~32% works
  // for the common 16/22/28/32 sizes without visible jaggies.
  const fontSize = Math.max(8, Math.round(size * 0.36));
  if (headshotUrl) {
    return (
      <img
        src={headshotUrl}
        alt=""
        aria-hidden
        style={style}
        className={
          'shrink-0 rounded-full object-cover ' +
          (onDark ? 'ring-1 ring-cream/40' : 'ring-1 ring-pine/25')
        }
        loading="lazy"
      />
    );
  }
  const fallbackClass = onDark
    ? 'bg-cream text-pine'
    : 'bg-pine/15 text-pine ring-1 ring-pine/25';
  return (
    <span
      aria-hidden
      style={{ ...style, fontSize }}
      className={
        'flex shrink-0 items-center justify-center rounded-full font-semibold leading-none ' +
        fallbackClass
      }
    >
      {initialsFor(name)}
    </span>
  );
}
